// "Which location do they work with?" (Stephanie 2026-10-20: "a new bozzuto
// email should be given the option of selecting or adding which location they
// work with. All Bozzuto locations have the same rep"). Shown on a website
// order or request from someone new whose company email domain is already in
// Flow. Staff pick one of the company's locations (or the main account), or
// add a new location — a sub-account under the main account that gets its rep.
// Used on Website orders (flow off) and in the website flow panel (flow on).
import { useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

export interface LocationAccount { id: string; name: string; repId: string | null; repName: string | null }
export interface CompanyLocations { main: LocationAccount; locations: LocationAccount[] }
export type LocationChoice = { clientId: string } | { mainId: string; name: string };

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink";
const repOf = (a: LocationAccount, main: LocationAccount) => a.repName ?? main.repName;

export function LocationPicker({
  companies,
  pickedId,
  disabled,
  waitingOnJake,
  onPick,
  onSeparate,
}: {
  companies: CompanyLocations[];
  /** The account already picked, if any. */
  pickedId: string | null;
  disabled?: boolean;
  /** The database step isn't run yet — show why it can't be picked. */
  waitingOnJake?: boolean;
  onPick: (choice: LocationChoice) => Promise<void>;
  /** "None of these — a separate new account" (Website orders only). */
  onSeparate?: () => void;
}) {
  const [value, setValue] = useState(pickedId ? `pick:${pickedId}` : "");
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const names = companies.map((c) => c.main.name).join(" / ");
  const all = companies.flatMap((c) => [{ a: c.main, main: c.main }, ...c.locations.map((l) => ({ a: l, main: c.main }))]);
  const picked = pickedId ? all.find((x) => x.a.id === pickedId) : undefined;
  const addingUnder = value.startsWith("new:") ? companies.find((c) => c.main.id === value.slice(4)) : undefined;

  const save = async (choice: LocationChoice) => {
    setSaving(true);
    setError(null);
    try {
      await onPick(choice);
      setNewName("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the location.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={cn("rounded-lg border bg-canvas p-4", picked ? "border-rule" : "border-warn")} aria-label="Location">
      <h3 className="flex items-center gap-1.5 text-[15px] font-bold">
        <MapPin className="h-4 w-4" aria-hidden /> Location
      </h3>
      {picked ? (
        <p className="mt-1">
          <span className="font-semibold">{picked.a.name}</span>
          {picked.a.id === picked.main.id ? " (main account)" : ` · ${picked.main.name}`}
          {repOf(picked.a, picked.main) ? <> — goes to <span className="font-semibold">{repOf(picked.a, picked.main)}</span></> : " — no rep on this account yet"}
        </p>
      ) : (
        <p className="mt-1 text-[13px] text-muted">
          Someone new at <span className="font-semibold text-ink">{names}</span> (same email domain). Which location do they work with?
          It goes to that location's rep.
        </p>
      )}
      {waitingOnJake ? (
        <p className="mt-2 text-[13px] text-muted">Picking the location turns on after a quick database step from Jake (it's on his list).</p>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              aria-label="Location"
              value={value}
              disabled={disabled || saving}
              onChange={(e) => {
                const v = e.target.value;
                setValue(v);
                setError(null);
                if (v.startsWith("pick:")) void save({ clientId: v.slice(5) });
              }}
              className={cn("h-10 min-w-[280px] rounded-md border border-rule bg-white px-3 text-[15px] text-ink", focusRing)}
            >
              <option value="">{picked ? "Change location…" : "Pick a location (required)"}</option>
              {companies.map((c) => (
                <optgroup key={c.main.id} label={c.main.name}>
                  <option value={`pick:${c.main.id}`}>{c.main.name} (main account)</option>
                  {c.locations.map((l) => (
                    <option key={l.id} value={`pick:${l.id}`}>
                      {l.name}
                    </option>
                  ))}
                  <option value={`new:${c.main.id}`}>+ Add a new {c.main.name} location</option>
                </optgroup>
              ))}
            </select>
            {saving ? <Loader2 className="h-4 w-4 animate-spin text-muted" aria-hidden /> : null}
          </div>
          {addingUnder ? (
            <form
              className="mt-3 flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void save({ mainId: addingUnder.main.id, name: newName });
              }}
            >
              <label className="block text-[13px]">
                <span className="font-semibold">New location name</span>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={200}
                  className="mt-1 min-w-[260px]"
                  placeholder={`e.g. ${addingUnder.main.name} – The Elms`}
                  autoFocus
                />
              </label>
              <Button type="submit" size="sm" disabled={saving || newName.trim().length < 2}>
                Add location
              </Button>
              <p className="w-full text-[12px] text-muted">
                Added under {addingUnder.main.name}
                {addingUnder.main.repName ? `, with ${addingUnder.main.repName} as the rep` : ""}.
              </p>
            </form>
          ) : null}
          {onSeparate ? (
            <button type="button" onClick={onSeparate} disabled={disabled || saving} className={cn("mt-2 block text-[13px] font-semibold underline underline-offset-2 hover:text-ink", focusRing)}>
              None of these — a separate new account
            </button>
          ) : null}
        </>
      )}
      {error ? <p role="alert" className="mt-1 text-[13px] font-semibold text-risk">{error}</p> : null}
    </section>
  );
}
